import { CSSProperties, useContext, useEffect } from 'react';
import { audioPath } from 'constants/audioConstants';
import { ConnectionManagerContext } from 'contexts/ConnectionManagerContext';
import GameContext from 'contexts/GameContext';
import { useOrientation } from 'hooks/useOrientation';
import useSound from 'hooks/useSound';

import { useVoteCountdown } from './useVoteCountdown';
import VoteRunAction from './VoteRunAction';

import 'components/UserActions/UserActions.scss';
import 'components/UserActions/PokerActions/PokerActions.scss';

/**
 * The geometry of `poker-action-row` in PokerActions/index.tsx, verbatim: same
 * gaps, same portrait full-width stretch. The vote buttons take over the footer
 * slot the action row has just vacated, so any difference here reads as the row
 * changing shape under the player mid-hand.
 */
const rowStyle = (isPortrait: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'row',
  gap: isPortrait ? '0.25rem' : '0.5rem',
  alignItems: 'center',
  justifyContent: 'center',
  width: isPortrait ? '100%' : undefined
});

// The two rules the action row's wrapper contributes that this panel also needs.
// `width` is load-bearing in BOTH orientations: the row above resolves its
// portrait `100%` against this element, so without it the buttons size to their
// labels instead of sharing the footer the way Fold/Check/Call do. And
// `poker-actions` sets no justify-content of its own, so landscape needs it
// stated to centre a row narrower than the full width.
//
// PokerActions also sets flex-direction and a gap on this div; both are inert for
// a wrapper with a single child, so they are left out rather than copied for the
// look of symmetry.
const WRAPPER_STYLE: CSSProperties = { width: '100%', justifyContent: 'center' };

/**
 * The multi-run vote row.
 *
 * EN-663 AC-5.1: the decision communicates through TURN PRESENTATION and SPEECH
 * BUBBLES, and nothing else. There is no prompt line here any more — no
 * "Multi-Run?", no "{name} wants Run It 2x", no "Waiting on N more players…".
 * The highlighted avatar and its countdown say whose decision it is, exactly as
 * they do preflop, and each seat's choice appears in its own bubble as it lands
 * (AC-2.4 / AC-3.4, rendered by SeatAction). A status line restating that was a
 * second, competing narration of one event — and the "asking player…" banner the
 * AC's no-go list rules out (N-8), over a "No" button it also rules out (N-9).
 *
 * Deleted with it: `firstVoterName`, `agreedCount` and `quorum`, which existed
 * only to word those sentences.
 *
 * `runCounts` is whatever THIS seat may choose — all three for the initiator,
 * 1X plus the offered count for a responder (`offeredRunCounts`, AC-2.2/3.2).
 * The panel renders the set it is given and decides nothing about it.
 */
const MultiRunActions = ({ runCounts }: { runCounts: number[] }) => {
  const { playerMultiRunVote } = useContext(GameContext);
  const hasVoted = playerMultiRunVote !== '0';
  const isPortrait = useOrientation() === 'portrait';

  // EN-10237: the window is seconds long and the player may well be looking at
  // another table, so the prompt announces itself out loud. `crossTable` opts
  // out of the focused-cell audio gate for exactly this reason — same reasoning
  // (and the same cue) as the turn announcement in PokerActions, which is what
  // AC-5.1's "standard turn sound cues are inherited unchanged" asks for.
  // `dedupeKey` is what stops a table's two mounted copies (grid cell + carousel
  // panel) from firing twice; it does NOT dedupe across tables, so two tables
  // opening a window both get heard.
  const [playVotePrompt] = useSound(audioPath.playersTurn, {
    playOnArrival: true,
    crossTable: true
  });
  useEffect(() => {
    if (hasVoted) return;
    // NO one-shot ref here, deliberately. This component MOUNTS at the moment
    // of its own cue — the same shape as WinnerMsg, and the exact case
    // useSound's dedupeKey docs call out: on the first render `sound` is still
    // null, so the call is dropped as `audio-not-loaded`, and a caller-held
    // "already asked" flag then refuses the retry once the Howl resolves. The
    // cue is silent every time. `play` is memoised on `sound`, so its identity
    // changes when the audio arrives and this effect re-fires; claimCue is what
    // makes that idempotent, and it is spent only on a play that actually
    // happens.
    playVotePrompt({ dedupeKey: 'multi-run-vote' });
  }, [hasVoted, playVotePrompt]);

  const { hasExpired } = useVoteCountdown();

  // The countdown is a LOCAL clock re-anchored on each broadcast. With the
  // connection down no broadcast ever re-anchors it, so it runs to zero on its
  // own — and withdrawing the buttons on that reading told the player the
  // window had shut when all that had actually happened was that we stopped
  // hearing. QA saw the buttons vanish and the timer sit at 0 with no way to
  // act and nothing said (Marlon 9/15).
  //
  // So expiry counts only while the action path is up. Zero on a live
  // connection really is the server's window closing, and that case is
  // unchanged: a click then is refused with `window_closed` by both writers,
  // with no reply channel to say so, which is why this gate exists at all.
  //
  // Offline the buttons stay, exactly as the Fold/Check/Call row stays through
  // a dropped connection, and a click gets the same answer that row gets: the
  // send guard refuses it and raises the server-timeout banner. A control that
  // is still there and explains itself beats one that silently disappears.
  const { actionPathStatus } = useContext(ConnectionManagerContext);
  const windowShut = hasExpired && actionPathStatus === 'ok';

  return (
    <div className='player-actions'>
      <div className='poker-actions' style={WRAPPER_STYLE}>
        {/*
          Once the countdown reaches 0 ON A LIVE CONNECTION the window is SHUT,
          but this panel stays mounted until the next broadcast clears `runs`. A
          click in that gap is refused by both writers with `window_closed`, and
          neither has a reply channel — so the client would optimistically
          render the vote as cast, refuse to re-send it, and the player would
          watch a window they were never entered into expire. Offering only what
          the server will still accept is the same rule `hasStakeInHand` applies
          to eligibility.

          The row is all this panel draws now that the prompt line is gone, so a
          shut window renders an empty action slot — which is exactly what the
          Fold/Check/Call row does when it is not your turn.

          See `windowShut` above for why the live-connection half matters: with
          the action path down, zero is the local clock running out, not the
          server closing the window.
        */}
        {!windowShut && (
          <div style={rowStyle(isPortrait)}>
            {runCounts.map((runs) => (
              <VoteRunAction key={runs} runs={runs} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiRunActions;
